export type VisualizationProofStoreKind = 'indexed-db' | 'local-storage' | 'memory';

export interface VisualizationCounter {
  key: string;
  kind?: 'campaign' | 'programmatic';
  mediaCampaignId?: string;
  programmaticDeliveryId?: string;
  authorizationId?: string;
  mediaId: string;
  identifier: string;
  instanceId?: string;
  occurrenceId?: string;
  playerProtocolVersion?: number;
  date: string;
  visualizations: number;
  updatedAt: number;
}

export interface SealedVisualizationProof {
  proofId: string;
  kind?: 'campaign' | 'programmatic';
  mediaCampaignId?: string;
  programmaticDeliveryId?: string;
  authorizationId?: string;
  mediaId: string;
  identifier: string;
  instanceId?: string;
  occurrenceId?: string;
  playerProtocolVersion?: number;
  date: string;
  visualizations: number;
  createdAt: number;
}

export interface VisualizationProofOutboxState {
  counters: VisualizationCounter[];
  proofs: SealedVisualizationProof[];
}

export interface VisualizationProofStore {
  readonly kind: VisualizationProofStoreKind;

  incrementCounter(counter: VisualizationCounter): Promise<void>;
  importState(state: VisualizationProofOutboxState): Promise<void>;
  exportState(): Promise<VisualizationProofOutboxState>;
  sealCounters(limit: number, instanceId?: string): Promise<void>;
  listProofs(limit: number, instanceId?: string): Promise<SealedVisualizationProof[]>;
  deleteProofs(proofIds: string[]): Promise<void>;
  clear(): Promise<void>;
  close(): void;
}

export function isVisualizationRecordEligibleForInstance(
  record: Pick<VisualizationCounter | SealedVisualizationProof, 'instanceId'>,
  instanceId?: string,
): boolean {
  return (
    instanceId === undefined || record.instanceId === undefined || record.instanceId === instanceId
  );
}

export interface LegacyVisualizationProofQueueItem {
  proofId: string;
  mediaCampaignId: string;
  date: string;
  mediaId: string;
  identifier: string;
  visualizations: number;
}

export const EMPTY_VISUALIZATION_OUTBOX_STATE: VisualizationProofOutboxState = {
  counters: [],
  proofs: [],
};

export function buildVisualizationCounterKey({
  kind,
  mediaCampaignId,
  programmaticDeliveryId,
  mediaId,
  date,
  instanceId,
  occurrenceId,
}: Pick<
  VisualizationCounter,
  | 'kind'
  | 'mediaCampaignId'
  | 'programmaticDeliveryId'
  | 'mediaId'
  | 'date'
  | 'instanceId'
  | 'occurrenceId'
>): string {
  return [
    kind ?? 'campaign',
    programmaticDeliveryId ?? mediaCampaignId,
    mediaId,
    date,
    instanceId,
    occurrenceId,
  ]
    .filter(Boolean)
    .join('\u001f');
}

export function createSealedVisualizationProof(
  counter: VisualizationCounter,
): SealedVisualizationProof {
  return {
    proofId: createProofId(),
    kind: counter.kind,
    mediaCampaignId: counter.mediaCampaignId,
    programmaticDeliveryId: counter.programmaticDeliveryId,
    authorizationId: counter.authorizationId,
    mediaId: counter.mediaId,
    identifier: counter.identifier,
    instanceId: counter.instanceId,
    occurrenceId: counter.occurrenceId,
    playerProtocolVersion: counter.playerProtocolVersion,
    date: counter.date,
    visualizations: counter.visualizations,
    createdAt: Date.now(),
  };
}

export function isLegacyVisualizationProofQueueItem(
  value: unknown,
): value is LegacyVisualizationProofQueueItem {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const proof = value as Record<string, unknown>;

  return (
    typeof proof['proofId'] === 'string' &&
    proof['proofId'].length > 0 &&
    typeof proof['mediaCampaignId'] === 'string' &&
    proof['mediaCampaignId'].length > 0 &&
    typeof proof['date'] === 'string' &&
    typeof proof['mediaId'] === 'string' &&
    typeof proof['identifier'] === 'string' &&
    typeof proof['visualizations'] === 'number' &&
    Number.isFinite(proof['visualizations']) &&
    proof['visualizations'] > 0
  );
}

export function legacyQueueItemToSealedProof(
  item: LegacyVisualizationProofQueueItem,
): SealedVisualizationProof {
  return {
    ...item,
    kind: 'campaign',
    createdAt: Date.now(),
  };
}

function createProofId(): string {
  const randomValues = new Uint32Array(2);

  try {
    globalThis.crypto?.getRandomValues(randomValues);
  } catch {
    randomValues[0] = Math.floor(Math.random() * 0xffffffff);
    randomValues[1] = Math.floor(Math.random() * 0xffffffff);
  }

  if (randomValues[0] === 0 && randomValues[1] === 0) {
    randomValues[0] = Math.floor(Math.random() * 0xffffffff);
    randomValues[1] = Math.floor(Math.random() * 0xffffffff);
  }

  return [
    'vizproof:v2',
    Date.now().toString(36),
    randomValues[0].toString(36),
    randomValues[1].toString(36),
  ].join(':');
}
